/* eslint-disable max-lines */
import GameService from '~/services/game.service';
import * as inboxDb from '~/lib/indexedDb/inbox';
import {
	MAIN_SCREEN_MODALS,
	TURN_PHASES,
	DRAGGABLE_COMPONENTS,
	LEVELS_TO_UNLOCK_FEATURES
} from '~/lib/constants';
import {
	employeesActions,
	gameActions,
	uiActions,
	customerActions,
	productsActions,
	inboxActions,
	tradeshowsActions,
	logbookActions,
	levelActions,
	playerHealthActions,
} from '~/store/actions';
import {
	characterSelectors,
	gameSelectors,
	levelSelectors,
	turnSelectors,
	financialStatementsSelectors,
	uiSelectors,
} from '~/store/selectors';
import { CreateTradeshowCustomer } from '~/models';
import FINANCIAL_STATEMENTS_TABS from '~/containers/financial-statments/TabEnum';
// --- internal imports -----

import * as types from './actionTypes';
import { getGamePlayStatsForTurn, resetTimer } from './utlis';
import { financialStatementsActions } from '../actions';
// --- local imports -----

function startNextTurn(caller) {
	return { type: types.TURN_START_NEXT, caller };
}

function displayPowerModalForGolofinIfSelected() {
	return async (dispatch, getState) => {
		const isGolofinSelected = characterSelectors.isGolofinSelected(getState());

		const isThirdTurn = turnSelectors.getTurnNum(getState()) === 3
			&& turnSelectors.isPreparationPhase(getState());

		if (isGolofinSelected && isThirdTurn) {
			dispatch(uiActions.changeActiveModal(
				MAIN_SCREEN_MODALS.POWER_NEXT_TURN
			));
		}
	};
}

function showFinancialStatementsIfNecessary(didQuarterEnd) {
	return async (dispatch, getState) => {
		if(!didQuarterEnd) {
			return;
		}
		
		const level = levelSelectors.getLevelNumber(getState());
		if (level < LEVELS_TO_UNLOCK_FEATURES.FINANCIAL_STATEMENTS_VIEW) {
			return;
		}

		dispatch(uiActions.openDraggable(DRAGGABLE_COMPONENTS.FINANCIAL_STATEMENTS));
	};
}

function prepareProductData(gameUser) {
	return async (dispatch) => {
		const mappedProducts = gameActions.mapProducts(gameUser.products);
		dispatch(productsActions.productsLoaded(mappedProducts));
		dispatch(productsActions.resetProductMonthlySales());
	};
}


function setupPreparationPhase(gameUser, notifications, didQuarterEnd) {
	return async (dispatch) => {
		inboxDb.readAllMessages();
		dispatch(financialStatementsActions.financialStatementsLoadForNextTurn(gameUser.financial_statements, didQuarterEnd));
		dispatch(playerHealthActions.update(gameUser.player_health));
		notifications && dispatch(inboxActions.handleNewMonthNotifications(notifications));
		dispatch(uiActions.displayMonthlyCashUpdates());
		dispatch(showFinancialStatementsIfNecessary(didQuarterEnd));

		dispatch(customerActions.skipCurrentCustomers());
		dispatch(prepareProductData(gameUser));
	};
}


function setUpCustomerPhase() {
	return async (dispatch, getState) => {
		dispatch(uiActions.resetDisplayedCashUpdates());
		if(!uiSelectors.shouldShowNewCustomerTypeHint(getState())) {
			dispatch(customerActions.prepareCustomers());
		}
	};
}

function setupNextTurn(gameUser, turn) {
	return async (dispatch) => {
		dispatch(displayPowerModalForGolofinIfSelected());
		dispatch(logbookActions.fetchUpdatedLogbook(gameUser.logbook_data));

		dispatch({ type: types.TURN_FETCHED_NEXT, turn });
		dispatch(gameActions.updateCash(gameUser.cash));
		dispatch(gameActions
			.updateStartedTurnWithAutoBookkeeper(gameUser.has_started_turn_with_auto_bookkeeper));
		dispatch(employeesActions.updateHiredEmployeesOnNewTurn());
	};
}

function fetchNextTurn() {
	return async (dispatch, getState) => {
		try {
			const previousPhase = turnSelectors.getTurnPhase(getState());
			dispatch({
				type: types.TURN_FETCHING_NEXT,
				isNextTurnLoading: true,
				previousPhase,
			});

			const didQuarterEnd = financialStatementsSelectors.didQuarterEnd(getState());

			const gameUserId = gameSelectors.getGameId(getState());
			const gamePlayStats = getGamePlayStatsForTurn(getState());	//	fetches time from local storage, and other metadata fields from store

			const oldTurnNumber = turnSelectors.getTurnNum(getState());

			const result = await GameService.updateTurn(
				gameUserId,
				gamePlayStats,
				oldTurnNumber,
				previousPhase
			);

			if (!result.isSuccess) {
				console.warn("REQUEST FAILED: GameService.updateTurn");
				dispatch({
					type: types.TURN_FETCHING_NEXT,
					isNextTurnLoading: false,
					previousPhase,
				});
				return;
			}

			const { updatedGameUser: gameUser, notifications } = result.data;
			const turn = _getTurnFromGameUser(gameUser);

			dispatch(setupNextTurn(gameUser, turn));
			if (turn.phase === TURN_PHASES.PREPARATION) {
				dispatch(setupPreparationPhase(gameUser, notifications, didQuarterEnd));
			}
			else {
				dispatch(customerActions.receivedTurnCustomers(gameUser.customers));

				if (gameUser.tradeshow_customers?.length > 0) {
					dispatch(tradeshowsActions.start(
						gameUser.tradeshow_customers.map(CreateTradeshowCustomer)
					));
				} else {
					dispatch(setUpCustomerPhase());
				}
			}

			resetTimer();

			if (didQuarterEnd) {
				dispatch(levelActions.handleEndOfQuarterLeveling(getState()));
				dispatch(financialStatementsActions.setDisplayedStatement(FINANCIAL_STATEMENTS_TABS.INCOME_STATEMENT));
			}

			dispatch({
				type: types.TURN_FETCHING_NEXT,
				isNextTurnLoading: false,
				previousPhase,
			});

			
		} catch (error) {
			console.error(error);
		}
	};
}

function updateByGameUser(gameUser) {
	return async (dispatch) => {
		if (gameUser === undefined || gameUser === null) {
			return null;
		}

		const turn = _getTurnFromGameUser(gameUser);

		dispatch({ type: types.TURN_STATUS_UPDATED, turn });
	};
}

function _getTurnFromGameUser(gameUser) {
	const shortYear = gameUser.turn_display.year.toString().substr(-2);

	return {
		num: gameUser.turns,
		phase: gameUser.turn_phase,
		date: `${gameUser.turn_display.date} ${shortYear}`,
		msg: gameUser.turn_msg,
	};
}

export function setHasPurchasedStockThisTurn(hasPurchasedStockThisTurn) {
	return {
		type: types.TURN_HAS_PURCHASED_STOCK_THIS_TURN,
		hasPurchasedStockThisTurn,
	};
}

export {
	startNextTurn,
	fetchNextTurn,
	updateByGameUser,
	setUpCustomerPhase,
	resetTimer,
};
